import React from "react";
import { Link } from "react-router-dom";
import {
  FolderLock,
  UploadCloud,
  FileText,
  ShieldCheck,
  Users,
  Search,
  ArrowRight,
  Lock,
  Eye,
  History
} from "lucide-react";

export default function DocumentVault() {
  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  return (
    <div className="min-h-screen bg-white" style={robotoStyle}>
      {/* Hero Section */}
      <div className="bg-white py-16 px-6 border-b border-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold uppercase tracking-wider mb-6" style={poppinsStyle}>
            <Lock size={14} />
            Enterprise-Grade Security
          </div>
          
          <h1 className="text-3xl md:text-4xl font-medium text-gray-900 mb-6 leading-tight" style={poppinsStyle}>
            Secure, Centralized & <br />
            Compliant Document Management
          </h1>
          
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Protect your sensitive organizational data with an encrypted vault. 
            Manage employee records, legal contracts, and company policies with 
            granular access controls and full audit trails.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-24">
        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mt-16 mb-20">
          <VaultFeature 
            icon={<FolderLock size={20} />} 
            title="Encrypted Storage"
            desc="AES-256 bit encryption for all files at rest and in transit."
            poppins={poppinsStyle}
          />
          <VaultFeature 
            icon={<Users size={20} />} 
            title="Granular Permissions"
            desc="Define view, edit, and delete rights at the folder and file level."
            poppins={poppinsStyle}
          />
          <VaultFeature 
            icon={<History size={20} />} 
            title="Version Control"
            desc="Track document iterations and revert to previous versions instantly."
            poppins={poppinsStyle}
          />
          <VaultFeature 
            icon={<FileText size={20} />} 
            title="Smart E-Folders"
            desc="Automated categorization for offer letters, IDs, and tax documents."
            poppins={poppinsStyle}
          />
          <VaultFeature 
            icon={<Search size={20} />} 
            title="Global Search"
            desc="Find any document instantly with metadata tags and employee filters."
            poppins={poppinsStyle}
          />
          <VaultFeature 
            icon={<ShieldCheck size={20} />} 
            title="Compliance Logs"
            desc="Complete audit history of who accessed which document and when."
            poppins={poppinsStyle}
          />
        </div>

        {/* Enterprise Context Blocks */}
        <div className="grid md:grid-cols-2 gap-12 py-12 border-t border-slate-100">
           <div className="space-y-6">
              <h2 className="text-xl font-medium text-gray-900" style={poppinsStyle}>The Digital HQ</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Move beyond scattered email threads and physical filing cabinets. 
                Our Document Vault provides a single source of truth for your HR, 
                Finance, and Admin teams.
              </p>
              <div className="space-y-3">
                 <BenefitItem label="Instant retrieval for internal and external audits." />
                 <BenefitItem label="Automated expiry alerts for contracts and IDs." />
                 <BenefitItem label="Secure employee self-service for payslip access." />
              </div>
           </div>

           <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4" style={poppinsStyle}>Privacy First</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                We understand the sensitivity of HR data. The vault is built to 
                be GDPR and SOC2 ready, ensuring that private employee information 
                remains visible only to authorized personnel.
              </p>
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200">
                 <Eye className="text-emerald-500" size={18} />
                 <span className="text-[11px] font-bold text-slate-700 uppercase" style={poppinsStyle}>Zero-Knowledge Architecture</span>
              </div>
           </div>
        </div>

        {/* Professional Compact CTA */}
        <div className="text-center pt-20 border-t border-slate-100">
          <h3 className="text-xl font-medium text-gray-900 mb-2" style={poppinsStyle}>Secure your organizational legacy</h3>
          <p className="text-sm text-gray-500 mb-10">Experience the peace of mind that comes with total document control.</p>
          
          <div className="flex justify-center">
            <Link 
              to="/login"
              className="group flex items-center gap-4 bg-gray-900 hover:bg-emerald-600 text-white px-6 py-2.5 rounded-full transition-all duration-300 shadow-md"
            >
              <div className="text-left">
                <p className="text-[9px] uppercase tracking-widest opacity-70 leading-none mb-1" style={poppinsStyle}>Access Vault</p>
                <p className="text-sm font-semibold leading-none" style={poppinsStyle}>Get Started Now</p>
              </div>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Internal Components */
function VaultFeature({ icon, title, desc, poppins }) {
  return (
    <div className="p-6 rounded-2xl border border-slate-100 hover:border-emerald-100 hover:bg-emerald-50/20 transition-all group">
      <div className="w-10 h-10 rounded-lg bg-slate-50 text-slate-600 group-hover:bg-emerald-100 group-hover:text-emerald-600 flex items-center justify-center mb-4 transition-colors">
        {icon}
      </div>
      <h4 className="font-medium text-gray-900 text-base mb-2" style={poppins}>{title}</h4>
      <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
    </div>
  );
}

function BenefitItem({ label }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );
}